<template>
  <div>
    <div class="card-body py-0 mt-n2 mb-3 d-flex align-items-center justify-content-end">
      <base-button @click="$router.push('/newInvoice')" size="sm">
        Nueva Factura de Anticipo
      </base-button>
    </div>
    <ag-grid-vue
      v-bind="agInvoices"
      style=" height: 70vh;"
      class="ag-theme-alpine"
      @grid-ready="onGridReady"
      @cell-clicked="onCellClicked"
      @row-double-clicked="onRowDoubleClicked"
    />
  </div>
</template>

<script>
/* eslint-disable vue/no-unused-components */

import { AgGridVue } from "ag-grid-vue";
import AgGrid from "@/mixins/AgGrid.js";
import Formats from "@/mixins/Formats";
import axios from "axios";
import AgBadge from "@/components/AgGrid/AgBadge.vue";
import BaseLoader from "@/components/BaseLoader.vue";
import { AG_GRID_LOCALE_ES } from '@ag-grid-community/locale';
import CustomerAddress from "@/components/AgGrid/CustomerAddress.vue";
import AgIcon from "@/components/AgGrid/AgIcon.vue";
import FilterDate from "@/components/AgGrid/Filters/FilterDate.vue";

export default {
  mixins: [AgGrid, Formats],
  components: {
    AgGridVue,
    AgBadge,
    BaseLoader,
    CustomerAddress,
    AgIcon,
    agDateInput: FilterDate,
  },
  data() {
    return {
      user: null,
      salesPersonOptions: null,
      downloadingPDF: null,
      downloadingXml: null,
      agInvoices: {
        columnDefs: [
          { 
            field: "DocNum", 
            headerName: "# Factura", 
            width: 90,
            filter: "agTextColumnFilter",
            cellClass: "ag-clickable-cell text-blue font-weight-bold",
          },
          {
            field: "customer", 
            headerName: "Cliente", 
            width: 300,
            filter: "agTextColumnFilter",
            cellRenderer: function ({data}) {
              return `
                <div>
                  <span class="font-weight-bold">${data.CardName}</span>
                  <span class="font-weight-normal">${data.CardCode}</span>
                </div>
              `;
            }
          },
          {
            field: "DocDate",
            headerName: "Fecha",
            width: 80,
            filter: "agDateColumnFilter",
            valueFormatter: ({value}) => this.dateFormat(value),
          },
          {
            field: "DocDueDate",
            headerName: "Vencimiento",
            width: 100,
            filter: "agDateColumnFilter",
            valueFormatter: ({value}) => this.dateFormat(value),
          },
          { 
            field: "SlpCode", 
            headerName: "Vendedor",
            width: 120,
            valueFormatter: this.salesPersonFormatter,
            filter: 'agSetColumnFilter',
            filterParams: {
              values: (params) => {
                if(this.salesPersonOptions){
                  params.success(this.salesPersonOptions.map((x) => x.SalesEmployeeCode));
                } else {
                  this.watcherSalesPersons = this.$watch(() => this.salesPersonOptions, () => {
                    params.success(this.salesPersonOptions.map((x) => x.SalesEmployeeCode));
                    this.watcherSalesPersons(); // Remove watcher
                  })
                }
              },
              valueFormatter: this.salesPersonFormatter,
              defaultToNothingSelected: true,
              applyMiniFilterWhileTyping: true,
            },
          },
          {
            field: "total",
            headerName: "Total",
            width: 120,
            filter: "agNumberColumnFilter",
            valueFormatter: ({data}) => this.currencyFormat(data.total, data.DocCur),
            headerClass: "ag-right-aligned-header",
            type: "rightAligned",
          },

          {
            field: "status",
            headerName: "Estatus",
            width: 80,
            filter: 'agSetColumnFilter',
            cellClass: 'd-flex justify-content-center',
            filterParams: {
              values: ['Abierta', 'Cerrada', 'Cancelada'],
              defaultToNothingSelected: true,
              applyMiniFilterWhileTyping: true,
            },
            cellRendererSelector: ({value}) => {
              const colors = {
                "Abierta": "#5e72e4",
                "Cerrada": "#adb5bd",
                "Cancelada": "#f5365c",
              }
              return {
                component: "AgBadge",
                params: {
                  color: colors[value],
                }
              }
            },
          },
          {
            field: "Comments",
            headerName: "Comentarios",
            width: 300,
            filter: "agTextColumnFilter",
          },
          {
            field: "Address2", 
            headerName: "Dirección", 
            width: 600,
            filter: "agTextColumnFilter",
            cellRenderer: function ({data}) {
              return `
                <span>
                  <span class="font-weight-bold">${data.ShipToCode}</span>
                  <span class="font-weight-normal">${data.Address2}</span>
                </span>
              `;
            }
          },
          {
            colId: "pdf",
            headerName: "PDF",
            sortable: false,
            filter: false,
            pinned: "right",
            width: 80,
            suppressHeaderMenuButton: true,
            cellClass: (params) => {
              const classList = ['d-flex', 'justify-content-center', 'align-items-center'];
                if(this.downloadingPDF){
                  if(this.downloadingPDF === params.data.DocNum){
                    classList.push('opacity-5');
                  }
                } else {
                  classList.push('ag-clickable-cell');
                }
                return classList.join(' ');
            },
            cellRendererSelector: () => {
              return {
                component: "AgIcon",
                params: {
                  color: "red",
                  text: "PDF",
                  icon: "fas fa-file-pdf",
                },
              };
            },
          },
          {
            colId: "xml",
            headerName: "XML",
            pinned: "right",
            sortable: false,
            filter: false,
            width: 80,
            suppressHeaderMenuButton: true,
            cellClass: (params) => {
              const classList = ['d-flex', 'justify-content-center', 'align-items-center'];
              if(this.downloadingXml){
                if(this.downloadingXml === params.data.DocNum){
                  classList.push('opacity-5');
                }
              } else {
                classList.push('ag-clickable-cell');
              }
              return classList.join(' ');
            },
            cellRendererSelector: () => {
              return {
                component: "AgIcon",
                params: {
                  color: "orange",
                  text: "XML",
                  icon: "fas fa-file-code",
                },
              };
            },
          },
        ],
        defaultColDef: {
          menuTabs: ["filterMenuTab"],
          cellDataType: false,
          floatingFilter: true,
          suppressFloatingFilterButton: true,
        },
        getRowId: ({data}) => data.DocNum,
        rowModelType: "serverSide",
        cacheBlockSize: 30,
        maxBlocksInCache: 10,
        pagination: true,
        paginationPageSize: 1000,
        paginationPageSizeSelector: [100, 500, 1000, 2000],
        loadingOverlayComponent: "BaseLoader",
        suppressCellFocus: true,
        enableCellTextSelection: true,
        localeText: AG_GRID_LOCALE_ES,
        alwaysShowVerticalScroll: true,
        rowSelection: "single",
      },
    };
  },
  created(){
    this.BASE_URL = process.env.VUE_APP_BASE_URL;
    this.HANA_URL = process.env.VUE_APP_HANA_URL;
    this.DATABASE = process.env.VUE_APP_DATABASE;
  },
  methods: {
    onGridReady({api}) {
      api.setGridOption("serverSideDatasource", this.getServerSideDatasource());
      axios
      .get(`/api/sales_persons`)
      .then((response) => {
        this.salesPersonOptions = response.data.value;
        api.refreshCells({columns: ['SlpCode']});
      })
      .catch((error) => {
        console.error(error);
      });
    },
    getServerSideDatasource() {
      return {
        getRows: (params) => {
          const { filterModel, endRow, startRow, sortModel } = params.request;
          //SUBQUERY
          const subQuery = `
            SELECT 
            "CardCode", 
            "DocNum",  
            "DocEntry", 
            "DocDate", 
            "DocDueDate", 
            "CardName", 
            "SlpCode", 
            "DocStatus", 
            "DocCur", 
            "ShipToCode", 
            "Address2", 
            "Comments", 
            "U_WebXML", 
            "U_TS_ReportID",


            ("CardCode" || ' ' || "CardName") AS "customer",
            CASE WHEN "DocCur" = 'MXP' THEN "DocTotalSy" ELSE "DocTotalFC" END AS "total",
            CASE WHEN "CANCELED" != 'N' THEN 'Cancelada'
            ELSE
              CASE
                WHEN "DocStatus" = 'O' THEN 'Abierta'
                ELSE 'Cerrada'
              END
            END AS "status"
            FROM ${this.DATABASE}."ODPI"
          `;
          //FILTERS
          let filters = ``;
          const filterModelKeys = Object.keys(filterModel);
          if (filterModelKeys.length) {
            const all = filterModelKeys.reduce((acc, key) => {
              if (filterModel[key].operator) {
                const conditionsArray = filterModel[key].conditions.map((condition) => {
                  return this.formatCondition(condition, key);
                }
              );
              const toPush = "(" + conditionsArray.join(" " + filterModel[key].operator + " ") + ")";
              acc.push(toPush);
              } else {
                const condition = this.formatCondition(filterModel[key], key);
                if(condition){
                  acc.push(condition);
                }
              }
              return acc;
            }, []);
            if(all.length){
              filters += "WHERE " + all.join(" AND ");
            }
          }
          //PAGINATION
          const pageSize = endRow - startRow;
          const pagination = `LIMIT ${pageSize} OFFSET ${startRow}`;
          //SORTING
          let sorting;
          if (sortModel.length) {
            sorting = `ORDER BY "${sortModel[0].colId}" ${sortModel[0].sort.toUpperCase()}`;
            if(sortModel[0].colId !== "DocNum"){
              sorting += `, "DocNum" DESC`;
            }
          } else {
            sorting = `ORDER BY "DocNum" DESC`;
          }
          //JSON PAYLOADS
          const rowDataJson = {
            query: `
              SELECT *
              FROM (${subQuery})
              ${filters}
              ${sorting}
              ${pagination}
            `
          };
          const totalsJson = {
            query: `
              SELECT COUNT(*) AS "rowCount"
              FROM (${subQuery})
              ${filters}
            `
          };
          // console.log("🟦rowDataJson", rowDataJson.query);
          // console.log("🟦totalsJson", totalsJson.query);
          axios
            .all([
              axios.post(`${this.HANA_URL}/api/hana/freedom`, rowDataJson),
              axios.post(`${this.HANA_URL}/api/hana/freedom`, totalsJson),
            ])
            .then(
              axios.spread((rowDataRes, totalsRes) => {
                // console.log("🟪 DATA", rowDataRes.data);
                // console.log("🟪 TOTALS", totalsRes.data);
                params.success({
                  rowData: rowDataRes.data,
                  rowCount: totalsRes.data[0].rowCount,
                });
              })
            )
            .catch((err) => {
              console.error(err);
              params.fail();
            });
        },
      };
    },
    handleClick(data) {
      this.$router.push("/advanceInvoice/" + data.DocNum);
    },
    onRowDoubleClicked(params) {
      this.handleClick(params.data);
    },
    onCellClicked(params){
      if(params.colDef.field === 'DocNum'){
        this.handleClick(params.data);
      } else
      if(params.column.colId === 'pdf' && !this.downloadingPDF){
        this.handlePDF(params);
      } else if(params.column.colId === 'xml'){
        this.handleXML(params);
      }
    },
    salesPersonFormatter({value}){
      if(!this.salesPersonOptions) return value;
      const salesPerson = this.salesPersonOptions.find(x => x.SalesEmployeeCode === value);
      return salesPerson ? salesPerson.SalesEmployeeName : value;
    },
    handlePDF(params){
      this.downloadingPDF = params.data.DocNum;
      params.api.refreshCells({columns: ['pdf']});
        const json = {
          "DocCode": "DPI20015",
          "Pairs": {
            "DocKey@": params.data.DocEntry
          }
        };
        axios.post(`/api/pdf_file`,json)
        .then(res => {
          if(res.data.se_err){
            this.$swal("Error", res.data.se_err.message, "error");
          } else {
            let link = document.createElement('a');
            link.download = `Factura_anticipo_${params.data.DocNum}.pdf`;
            link.href =  res.data.base64;
            link.click();
          }
        }).catch(error => {
          console.error(error);
        }).finally(() => {
          this.downloadingPDF = null;
          params.api.refreshCells({columns: ['pdf']});
        });
    },
    handleXML(params){
      if(!this.downloadingXml){
        if(params.data.U_WebXML){
          let link_xml = document.createElement('a');
          link_xml.download = `Factura_anticipo_${params.data.DocNum}.xml`;
          link_xml.href = params.data.U_WebXML;
          link_xml.target = '_blank';
          document.body.appendChild(link_xml);
          link_xml.click();
          document.body.removeChild(link_xml);
        } else if (params.data.U_TS_ReportID){
          const [año, mes, dia] = params.data.DocDate.split("-");
          const download = `https://canplast.net:50000/api/v1/sap/xml/${año}/${mes}/${dia}/${params.data.CardCode}/${params.data.CardName}/${params.data.U_TS_ReportID}?token=${localStorage.getItem('token-cpm')}`
          window.open(download, '_blank');
        } else {
          this.downloadingXml = params.data.DocNum;
          params.api.refreshCells({columns: ['xml']});
          fetch(`https://canplast.net:50000/api/v1/sap/GET/Invoice/UUID/${params.data.DocNum}`, {
            method: 'GET',
            headers: {
              'Accept': 'application/json',
              'Content-Type': 'application/json',
              'x-access-token': localStorage.getItem('token-cpm')
            }
          })
          .then((response) => response.json())
          .then((responseJson) => {
            if(responseJson.UUID){
              const [año, mes, dia] = params.data.DocDate.split("-");
              const download = `https://canplast.net:50000/api/v1/sap/xml/${año}/${mes}/${dia}/${params.data.CardCode}/${params.data.CardName}/${responseJson.UUID}?token=${localStorage.getItem('token-cpm')}`
              window.open(download, '_blank');
            } else {
              this.$swal("Error", "No se pudo obtener el XML", "error");
            }
          })
          .catch((error) => {
            console.error(error);
          }).finally(() => {
            this.downloadingXml = null;
            params.api.refreshCells({columns: ['xml']});
          });
        }
      }
    },
  },
};
</script>

<style lang="scss">
.ag-clickable-cell {
  cursor: pointer;
}
.ag-clickable-cell:hover {
  background: #e9ecef;
}
</style>
